import { useEffect, useState } from "react";
import Loader from "../reusable_components/Loader";
import DataCard from "../reusable_components/DataCard";
import { title, pageTitle, sectionTitle } from "../utils/style";
import { url } from "../utils/config";
import { FaCarSide } from "react-icons/fa";
import { RiAlertLine } from "react-icons/ri";
import { LiaTintSolid } from "react-icons/lia";
import { FiClock, FiTool, FiCheckCircle } from "react-icons/fi";
import fetchData from "../custom_functions/fetchData";
import Table from "../reusable_components/Table";
import { toast } from "react-toastify";
import MaintenanceCostsChart from "../reusable_components/FuelMaintenanceChart";
import VehicleServiceList from "../reusable_components/VehicleServiceList";

function Dashboard() {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getDashData();
    }, [])

    async function getDashData() {
        try {

            setLoading(true);
            fetchData(url + '/dashboard', (error) => {
                toast.error(error.response.data.message, { autoClose: 3000 });
                setLoading(false);
            }, (data) => {
                setData(data);
                setLoading(false);
            },);
        } catch (error) {
            toast.error("Something went wrong", { autoClose: 3000 });
            setLoading(false);
        }
    }

    const options = [
        { header: "Make", key: "make" },
        { header: "Model", key: "model" },
        { header: "Year", key: "year" },
        { header: "VIN", key: "vin" },
        { header: "Status", key: "status" },]

    return (
        loading ? (
            <Loader />
        ) : (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={pageTitle}>Dashboard</h1>
                        <p className="text-neutral-600 mt-1">Welcome back to your fleet management</p>
                    </div>
                </div>

                {/* Key Metrics - Top */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <DataCard
                        title={'Active Vehicles'}
                        value={data?.activeVehicleCount}
                        icon={<FaCarSide size={28} className="text-primary" />}
                    />
                    <DataCard
                        title={'Pending Reminders'}
                        value={data?.pendingRemindersCount}
                        icon={<RiAlertLine size={28} className="text-danger" />}
                    />
                    <DataCard
                        title={'Service Cost'}
                        value={`$${(data?.totalServiceCost || 0).toFixed(2)}`}
                        icon={<LiaTintSolid size={28} className="text-secondary" />}
                    />
                </div>

                {/* Service Summary */}
                <div className="bg-white rounded-xl p-6 shadow-base border border-neutral-100">
                    <div className="mb-6">
                        <h2 className="text-xl font-bold text-neutral-900">Service Request Summary - This Month</h2>
                        <p className="text-sm text-neutral-600 mt-1">Overview of service requests</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-xl border border-accent-light bg-accent-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-light text-accent">
                                    <FiClock size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-accent">Pending</div>
                                    <div className="text-2xl font-bold text-neutral-900">{data?.pendingServiceRequestCount || 0}</div>
                                    <div className="text-xs text-neutral-500">Awaiting review</div>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl border border-primary-light bg-primary-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-light text-primary">
                                    <FiTool size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-primary">In Service</div>
                                    <div className="text-2xl font-bold text-neutral-900">{data?.inServiceRequestCount || 0}</div>
                                    <div className="text-xs text-neutral-500">Currently processing</div>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl border border-secondary-light bg-secondary-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-light text-secondary">
                                    <FiCheckCircle size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-secondary">Completed</div>
                                    <div className="text-2xl font-bold text-neutral-900">{data?.completedServiceRequestCount || 0}</div>
                                    <div className="text-xs text-neutral-500">Finished services</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vehicle Status KPIs */}
                <div className="bg-white rounded-xl p-6 shadow-base border border-neutral-100">
                    <div className="mb-6">
                        <h2 className="text-xl font-bold text-neutral-900">Vehicle Status</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Active Vehicles KPI */}
                        <div className="rounded-xl border border-primary-light bg-primary-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-light text-primary">
                                    <FaCarSide size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-primary">Active</div>
                                    <div className="text-2xl font-bold text-neutral-900">
                                        {data?.activeVehicleCount || 0}
                                    </div>
                                    <div className="text-xs text-neutral-500">vehicles active</div>
                                </div>
                            </div>
                        </div>

                        {/* Inactive Vehicles KPI */}
                        <div className="rounded-xl border border-danger-light bg-danger-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-danger-light text-danger">
                                    <FaCarSide size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-danger">Inactive</div>
                                    <div className="text-2xl font-bold text-neutral-900">
                                        {data?.inactiveVehicleCount || 0}
                                    </div>
                                    <div className="text-xs text-neutral-500">vehicles inactive</div>
                                </div>
                            </div>
                        </div>

                        {/* Total Vehicles KPI */}
                        <div className="rounded-xl border border-secondary-light bg-secondary-light/30 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-light text-secondary">
                                    <FaCarSide size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-secondary">Total</div>
                                    <div className="text-2xl font-bold text-neutral-900">
                                        {data?.totalVehicleCount || 0}
                                    </div>
                                    <div className="text-xs text-neutral-500">total vehicles</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vehicle Service List */}
                <VehicleServiceList
                    pendingCount={data?.pendingServiceRequestCount || 0}
                    inServiceCount={data?.inServiceRequestCount || 0}
                    completedCount={data?.completedServiceRequestCount || 0}
                />

                {/* Charts - Stacked Vertically */}
                <div className="space-y-6">
                    {/* Service Costs Chart */}
                    <div className="bg-white rounded-xl p-3 sm:p-6 shadow-base border border-neutral-100">
                        <MaintenanceCostsChart />
                    </div>
                </div>

                {/* Recent Vehicles */}
                <div className="bg-white rounded-xl p-6 shadow-base border border-neutral-100">
                    <h3 className="text-lg font-bold text-neutral-900 mb-4">Recent Vehicles</h3>
                    <Table
                        tableData={data?.recentVehicleList || []}
                        pagination={false}
                        noButton={true}
                        url={''}
                        noFilter={true}
                        options={options}
                    />
                </div>
            </div>
        )
    );
}
export default Dashboard;